<template>
  <div class="pdf-page">
    <div class="pdf-tools">
      <button aria-label="返回" @click="goBack"><Icon type="ios-arrow-back" /></button>
      <span>{{ pageCount ? pageCount + ' 页' : '检测报告' }}</span>
      <button class="pdf-tools__open" @click="openOriginal">打开原报告</button>
    </div>

    <main ref="viewer" class="pdf-viewer">
      <div v-if="loading" class="pdf-loading">
        <div class="pdf-loading__sheet"><i></i><i></i><i></i><i></i></div>
        <span>正在加载报告...</span>
      </div>

      <div v-else-if="errorMessage" class="pdf-state pdf-state--error">
        <Icon type="ios-alert-outline" />
        <b>报告预览加载失败</b>
        <p>{{ errorMessage }}</p>
        <Button type="primary" @click="openOriginal">打开原报告</Button>
      </div>

      <template v-else>
        <div v-for="page in pages" :key="page" class="pdf-sheet">
          <canvas :ref="'pageCanvas' + page"></canvas>
          <span>{{ page }} / {{ pageCount }}</span>
        </div>
      </template>
    </main>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/es5/build/pdf'
// eslint-disable-next-line import/no-webpack-loader-syntax
import pdfWorkerSrc from 'file-loader?name=static/js/[name].[hash:7].[ext]!pdfjs-dist/es5/build/pdf.worker.min.js'

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc

export default {
  name: 'PdfPreview',
  data () {
    return {
      loading: true,
      errorMessage: '',
      pages: [],
      pageCount: 0,
      loadingTask: null,
      renderTasks: []
    }
  },
  computed: {
    reportUrl () {
      return String(this.$route.query.url || '').trim()
    }
  },
  mounted () {
    this.loadPdf()
  },
  beforeDestroy () {
    this.renderTasks.forEach(task => {
      if (task && typeof task.cancel === 'function') task.cancel()
    })
    if (this.loadingTask && typeof this.loadingTask.destroy === 'function') {
      this.loadingTask.destroy()
    }
  },
  methods: {
    async loadPdf () {
      if (!this.reportUrl) {
        this.loading = false
        this.errorMessage = '未获取到报告地址'
        return
      }
      this.loading = true
      this.errorMessage = ''
      try {
        this.loadingTask = pdfjsLib.getDocument({
          url: this.reportUrl,
          withCredentials: false
        })
        const pdf = await this.loadingTask.promise
        this.pageCount = pdf.numPages
        this.pages = Array.from({ length: pdf.numPages }, (item, index) => index + 1)
        this.loading = false
        await this.$nextTick()
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          await this.renderPage(pdf, pageNumber)
        }
      } catch (error) {
        if (error && error.name === 'RenderingCancelledException') return
        this.loading = false
        this.errorMessage = this.resolveError(error)
      }
    },
    async renderPage (pdf, pageNumber) {
      const page = await pdf.getPage(pageNumber)
      const canvasRef = this.$refs['pageCanvas' + pageNumber]
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef
      if (!canvas) return

      const baseViewport = page.getViewport({ scale: 1 })
      const viewerWidth = this.$refs.viewer ? this.$refs.viewer.clientWidth : window.innerWidth
      const cssWidth = Math.max(280, Math.min(viewerWidth - 24, 900))
      const cssScale = cssWidth / baseViewport.width
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
      const viewport = page.getViewport({ scale: cssScale * pixelRatio })
      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)
      canvas.style.width = Math.floor(viewport.width / pixelRatio) + 'px'
      canvas.style.height = Math.floor(viewport.height / pixelRatio) + 'px'

      const renderTask = page.render({
        canvasContext: canvas.getContext('2d'),
        viewport: viewport
      })
      this.renderTasks.push(renderTask)
      await renderTask.promise
    },
    resolveError (error) {
      const message = error && error.message ? error.message : ''
      if (/cors|failed to fetch|unexpected server response/i.test(message)) {
        return '报告文件跨域访问受限，请点击“打开原报告”查看'
      }
      return message || '报告文件无法加载，请点击“打开原报告”查看'
    },
    openOriginal () {
      if (!this.reportUrl) {
        this.$Message.warning('未获取到报告地址')
        return
      }
      window.location.href = this.reportUrl
    },
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/home')
      }
    }
  }
}
</script>

<style scoped>
.pdf-page { min-height: 100vh; background: #e9edf3; }
.pdf-tools { position: sticky; top: 0; z-index: 10; height: 46px; padding: 0 12px; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; background: rgba(255, 255, 255, .96); border-bottom: 1px solid #dfe4eb; }
.pdf-tools button { border: 0; background: none; color: #0f51b1; text-align: left; font-size: 22px; }
.pdf-tools > span { color: #697386; font-size: 12px; }
.pdf-tools .pdf-tools__open { text-align: right; font-size: 13px; }
.pdf-viewer { max-width: 920px; min-height: calc(100vh - 46px); margin: 0 auto; padding: 12px; box-sizing: border-box; }
.pdf-state { min-height: 300px; padding: 40px 20px; border-radius: 10px; background: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: #6b7687; text-align: center; }
.pdf-state > i { font-size: 42px; color: #2f78d0; }
.pdf-state__loading { animation: pdf-rotate 1s linear infinite; }
.pdf-loading { min-height: 430px; display: flex; flex-direction: column; align-items: center; gap: 16px; color: #7a8493; font-size: 13px; }
.pdf-loading__sheet { width: min(82vw, 520px); height: 390px; padding: 32px 28px; box-sizing: border-box; background: #fff; }
.pdf-loading__sheet i { display: block; height: 12px; margin-bottom: 20px; border-radius: 6px; background: linear-gradient(90deg, #edf0f4 25%, #f7f8fa 50%, #edf0f4 75%); background-size: 200% 100%; animation: pdf-shimmer 1.4s infinite; }
.pdf-loading__sheet i:nth-child(1) { width: 44%; height: 20px; }
.pdf-loading__sheet i:nth-child(3) { width: 76%; }
.pdf-loading__sheet i:nth-child(4) { width: 88%; }
.pdf-state--error p { max-width: 440px; margin: 0; line-height: 1.7; }
.pdf-sheet { position: relative; margin-bottom: 12px; overflow: hidden; border-radius: 4px; background: #fff; box-shadow: 0 2px 8px rgba(19, 43, 75, .12); text-align: center; }
.pdf-sheet canvas { display: block; max-width: 100%; margin: 0 auto; background: #fff; }
.pdf-sheet span { position: absolute; right: 8px; bottom: 8px; padding: 2px 7px; border-radius: 10px; color: #fff; background: rgba(0, 0, 0, .45); font-size: 11px; }
@keyframes pdf-rotate { to { transform: rotate(360deg); } }
@keyframes pdf-shimmer { to { background-position: -200% 0; } }
</style>



// WEBPACK FOOTER //
// src/views/PdfPreview.vue